/**
 * Prediction market types and SDK mapping.
 *
 * Defines the supported prediction markets and maps them to the
 * FansUnited SDK's MarketEnum for submission, and to the odds market
 * string used by `useBatchMatchOdds` for fetching.
 */

import { MarketEnum } from "fansunited-sdk-esm";
import type { MarketType } from "fansunited-data-layer";

/**
 * All prediction markets the hook supports.
 * Extend this union when adding new markets.
 */
export type PredictionMarket =
    | "CORRECT_SCORE"
    | "CORRECT_SCORE_ADVANCED"
    | "CORRECT_SCORE_HT"
    | "FT_1X2"
    | "DOUBLE_CHANCE"
    | "BOTH_TEAMS_SCORE"
    | "OVER_UNDER";

/**
 * Map a PredictionMarket to the SDK's MarketEnum used in
 * `sdk.predictor.makeFootballPrediction()`.
 */
export function getSdkMarketEnum(market: PredictionMarket): MarketEnum {
    switch (market) {
        case "CORRECT_SCORE":
            return MarketEnum.CORRECT_SCORE;
        case "CORRECT_SCORE_ADVANCED":
            return MarketEnum.CORRECT_SCORE_ADVANCED;
        case "CORRECT_SCORE_HT":
            return MarketEnum.CORRECT_SCORE_HT;
        case "FT_1X2":
            return MarketEnum.FT_1X2;
        case "DOUBLE_CHANCE":
            return MarketEnum.DOUBLE_CHANCE;
        case "BOTH_TEAMS_SCORE":
            return MarketEnum.BOTH_TEAMS_SCORE;
        case "OVER_UNDER":
            return MarketEnum.OVER_GOALS_2_5;
        default:
            return MarketEnum.CORRECT_SCORE_ADVANCED;
    }
}

/**
 * Default odds market string for `useBatchMatchOdds({ markets })`.
 * Can be overridden per-call via `oddsMarket` param.
 */
export function getDefaultOddsMarket(market: PredictionMarket): MarketType {
    switch (market) {
        case "CORRECT_SCORE":
        case "CORRECT_SCORE_ADVANCED":
        case "CORRECT_SCORE_HT":
            // Score-based markets derive "1"/"X"/"2" outcome keys, so we need
            // FT_1X2 odds to match against — not CORRECT_SCORE scoreline odds.
            return "1x2";
        case "FT_1X2":
            return "1x2";
        case "DOUBLE_CHANCE":
            return "DOUBLE_CHANCE";
        case "BOTH_TEAMS_SCORE":
            return "BOTH_TO_SCORE";
        case "OVER_UNDER":
            return "OVER_UNDER";
        default:
            return "1x2";
    }
}

/**
 * Whether a market uses score-based input (two numbers) vs. selection-based (single string).
 */
export function isScoreBasedMarket(market: PredictionMarket): boolean {
    return market === "CORRECT_SCORE" || market === "CORRECT_SCORE_ADVANCED" || market === "CORRECT_SCORE_HT";
}
